import { confirmUnsavedChangesOnLeave } from '../../shared/components/unsaved-changes-dialog/unsaved-changes.registry';
import { NgModule } from '@angular/core';
import { provideRouter, Route } from '@angular/router';
import { SystemAdminAuthGuard } from './guards/system-admin-auth.guard';
import { testModeGuard } from './guards/test-mode.guard';
import { SystemAdminLayoutComponent } from './components/system-admin-layout/system-admin-layout.component';

const testingRoute: Route = {
  path: 'testing',
  canActivate: [testModeGuard],
  loadComponent: () => import('./pages/testing/testing.component'),
  children: [
    { path: '', redirectTo: 'mock-email', pathMatch: 'full' },
    {
      path: 'mock-email',
      loadComponent: () => import('./pages/testing/mock-email/mock-email.component'),
    },
    {
      path: 'date-override',
      loadComponent: () => import('./pages/testing/date-override/date-override.component'),
    },
    {
      path: 'mock-invoices',
      loadComponent: () => import('./pages/testing/mock-invoices/mock-invoices.component'),
    },
    {
      path: 'mock-customers',
      loadComponent: () => import('./pages/testing/mock-customers/mock-customers.component'),
    },
    {
      path: 'mock-history',
      loadComponent: () => import('./pages/testing/mock-history/mock-history.component'),
    },
  ],
};

@NgModule({
  providers: [
    provideRouter([
      {
        path: 'system-admin',
        component: SystemAdminLayoutComponent,
        canActivate: [SystemAdminAuthGuard],
        children: [
          { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
          {
            path: 'dashboard',
            loadComponent: () => import('./pages/dashboard/dashboard.component'),
          },
          testingRoute,
          {
            path: 'security',
            loadComponent: () => import('./pages/security/security.component'),
            children: [
              { path: '', redirectTo: 'users', pathMatch: 'full' },
              {
                path: 'users',
                loadComponent: () => import('./pages/security/users/users.component'),
              },
              {
                path: 'audit',
                loadComponent: () => import('./pages/security/audit/audit.component'),
              },
              {
                path: 'audit/:userId',
                loadComponent: () => import('./pages/security/audit/audit.component'),
              },
              {
                path: 'role-groups',
                loadComponent: () => import('./pages/security/role-groups/role-groups.component'),
              },
              {
                // Platform-wide email log. SYSTEM_ADMIN comes from the
                // `system-admin` root's SystemAdminAuthGuard, which every child
                // inherits — the endpoints are gated server-side too.
                path: 'email-audit',
                loadComponent: () => import('./pages/security/email-audit/email-audit.component'),
              },
              {
                // Platform-wide notification log. Same SYSTEM_ADMIN inheritance
                // as the sibling above: the `system-admin` root's guard covers
                // the route, and the endpoints are gated server-side too.
                path: 'notification-audit',
                loadComponent: () =>
                  import('./pages/security/notification-audit/notification-audit.component'),
              },
              {
                // Uploaded agent-desktop logs. SYSTEM_ADMIN via the root guard, and server-side.
                path: 'agent-logs',
                loadComponent: () => import('./pages/security/agent-logs/agent-logs.component'),
              },
            ],
          },
          {
            path: 'profiles',
            loadComponent: () => import('./pages/profiles/profiles.component'),
            // Unsaved AutoPay settings / term rules must be saved or discarded before leaving.
            canDeactivate: [confirmUnsavedChangesOnLeave],
          },
          {
            // Billing moved to the main app (/billing, Freely and FRB profiles only).
            // Kept as a redirect so old links and bookmarks still land somewhere.
            path: 'billing',
            redirectTo: '/billing',
            pathMatch: 'full',
          },
          {
            path: 'settings',
            loadComponent: () => import('./pages/settings/settings.component'),
            children: [
              { path: '', redirectTo: 'environment', pathMatch: 'full' },
              {
                path: 'environment',
                loadComponent: () => import('./pages/settings/environment/environment.component'),
              },
              {
                path: 'global-settings',
                loadComponent: () => import('./pages/settings/global-settings/global-settings.component'),
                children: [
                  { path: '', redirectTo: 'sftp', pathMatch: 'full' },
                  {
                    path: 'sftp',
                    loadComponent: () =>
                      import('./pages/settings/global-settings/freely-sftp-connections.component').then(
                        (m) => m.FreelySftpConnectionsComponent
                      ),
                  },
                  {
                    path: 'job-maintenance',
                    loadComponent: () =>
                      import('./pages/settings/global-settings/global-job-maintenance.component'),
                  },
                  {
                    path: 'paya-ach',
                    loadComponent: () =>
                      import('./pages/settings/global-settings/paya-ach-ingestion.component'),
                  },
                ],
              },
              {
                path: 'card-bins',
                loadComponent: () => import('./pages/settings/card-bins/card-bins.component'),
              },
              {
                path: 'email-templates',
                loadComponent: () => import('./pages/settings/global-email-templates/global-email-templates.component'),
                data: { lockedMode: 'global' },
              },
              {
                path: 'system-jobs',
                loadComponent: () => import('./pages/settings/system-jobs/system-jobs.component'),
              },
              {
                path: 'sftp-accounts',
                loadComponent: () =>
                  import('./pages/settings/sftp-accounts/sftp-accounts.component').then(
                    (m) => m.SftpAccountsComponent
                  ),
              },
              {
                path: 'file-system',
                loadComponent: () => import('./pages/settings/file-system/file-system.component'),
              },
            ],
          },
        ],
      },
    ]),
  ],
})
export class SystemAdminRoutingModule {}
